import { useState, useEffect, useCallback } from "react";
import api from "../api.js";
import styled from "styled-components";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Titulo = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #1a5276;
  margin: 0 0 4px 0;
`;

const FiltroBar = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const BtnFiltro = styled.button`
  padding: 8px 18px;
  border: 2px solid #2980b9;
  border-radius: 20px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  background-color: ${({ $ativo }) => ($ativo ? "#2980b9" : "white")};
  color: ${({ $ativo }) => ($ativo ? "white" : "#2980b9")};
  transition: all 0.2s;

  &:hover {
    background-color: #2980b9;
    color: white;
  }
`;

const Tabela = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const Th = styled.th`
  background-color: #1a5276;
  color: white;
  padding: 8px 10px;
  text-align: left;
  font-weight: 600;
`;

const Td = styled.td`
  padding: 8px 10px;
  border-bottom: 1px solid #e0e0e0;
  vertical-align: middle;
`;

const Tr = styled.tr`
  background-color: ${({ $par }) => ($par ? "#f8f9fa" : "white")};
  &:hover {
    background-color: #eaf4fb;
  }
`;

const Badge = styled.span`
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  background-color: ${({ $status }) => {
    switch ($status) {
      case "PENDENTE": return "#fff3cd";
      case "PROCESSANDO": return "#cce5ff";
      case "APROVADO":
      case "PAGO": return "#d4edda";
      case "REJEITADO":
      case "CANCELADO": return "#f8d7da";
      case "EXPIRADO": return "#e2e3e5";
      default: return "#e2e3e5";
    }
  }};
  color: ${({ $status }) => {
    switch ($status) {
      case "PENDENTE": return "#856404";
      case "PROCESSANDO": return "#004085";
      case "APROVADO":
      case "PAGO": return "#155724";
      case "REJEITADO":
      case "CANCELADO": return "#721c24";
      case "EXPIRADO": return "#383d41";
      default: return "#383d41";
    }
  }};
`;

const TipoBadge = styled.span`
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  background-color: ${({ $tipo }) => $tipo === "DEPOSITO" ? "#d4edda" : "#fce8e6"};
  color: ${({ $tipo }) => $tipo === "DEPOSITO" ? "#155724" : "#7b1f1f"};
`;

const Resumo = styled.div`
  background: #eaf4fb;
  border: 1px solid #aed6f1;
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 14px;
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
`;

const ResumoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  span:first-child {
    font-size: 11px;
    color: #666;
    text-transform: uppercase;
  }
  span:last-child {
    font-weight: 700;
    font-size: 16px;
    color: #1a5276;
  }
`;

const Vazio = styled.div`
  text-align: center;
  color: #888;
  padding: 32px;
  font-style: italic;
`;

const BtnAprovar = styled.button`
  padding: 5px 12px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  background-color: #27ae60;
  color: white;

  &:hover { background-color: #1e8449; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

function fmtData(dt) {
  if (!dt) return "-";
  return new Date(dt).toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit"
  });
}

function fmtValor(v) {
  return "R$ " + Number(v || 0).toFixed(2).replace(".", ",");
}

export default function AdminPix() {
  const [filtro, setFiltro] = useState("pendentes");
  const [saques, setSaques] = useState([]);
  const [depositos, setDepositos] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [processando, setProcessando] = useState({});

  const carregarDados = useCallback(async () => {
    setCarregando(true);
    try {
      const [resSaques, resDepositos] = await Promise.all([
        api.get("liga/admin/saques"),
        api.get("liga/admin/depositos"),
      ]);
      setSaques(resSaques.data || []);
      setDepositos(resDepositos.data || []);
    } catch (e) {
      console.error("Erro ao carregar dados PIX:", e);
    }
    setCarregando(false);
  }, []);

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  const itensExibidos = () => {
    if (filtro === "pendentes") {
      const saqPend = saques
        .filter(s => s.status === "PENDENTE" || s.status === "PROCESSANDO")
        .map(s => ({ ...s, tipo: "SAQUE", dataRef: s.dtSolicitacao }));
      const depPend = depositos
        .filter(d => d.status === "PENDENTE")
        .map(d => ({ ...d, tipo: "DEPOSITO", dataRef: d.dtCriacao }));
      return [...saqPend, ...depPend].sort(
        (a, b) => new Date(b.dataRef) - new Date(a.dataRef)
      );
    }
    if (filtro === "recebidos") {
      return depositos
        .filter(d => d.status === "APROVADO")
        .map(d => ({ ...d, tipo: "DEPOSITO", dataRef: d.dtPagamento || d.dtCriacao }))
        .sort((a, b) => new Date(b.dataRef) - new Date(a.dataRef));
    }
    if (filtro === "enviados") {
      return saques
        .filter(s => s.status === "PAGO")
        .map(s => ({ ...s, tipo: "SAQUE", dataRef: s.dtProcessamento || s.dtSolicitacao }))
        .sort((a, b) => new Date(b.dataRef) - new Date(a.dataRef));
    }
    return [];
  };

  const itens = itensExibidos();
  const totalValor = itens.reduce((acc, i) => acc + Number(i.valor || 0), 0);

  async function aprovarSaque(saqueId) {
    setProcessando(p => ({ ...p, [saqueId]: true }));
    try {
      const res = await api.post(`liga/admin/saques/${saqueId}/aprovar`);
      const { chavePix, valor, nomeUsuario } = res.data;
      alert(
        `Saque aprovado!\n\nTransfira manualmente via PIX:\n` +
        `Destinatário: ${nomeUsuario}\n` +
        `Chave PIX: ${chavePix}\n` +
        `Valor: R$ ${Number(valor).toFixed(2)}`
      );
      await carregarDados();
    } catch (e) {
      alert("Erro ao aprovar saque: " + (e.response?.data?.message || e.message));
    }
    setProcessando(p => ({ ...p, [saqueId]: false }));
  }

  return (
    <Container>
      <Titulo>Movimentações PIX</Titulo>

      <FiltroBar>
        {["pendentes", "recebidos", "enviados"].map(f => (
          <BtnFiltro
            key={f}
            $ativo={filtro === f}
            onClick={() => setFiltro(f)}
          >
            {f === "pendentes" && "Pendentes"}
            {f === "recebidos" && "Recebidos (depósitos)"}
            {f === "enviados" && "Enviados (saques)"}
          </BtnFiltro>
        ))}
        <BtnFiltro
          style={{ marginLeft: "auto", borderColor: "#888", color: "#888" }}
          $ativo={false}
          onClick={carregarDados}
          disabled={carregando}
        >
          {carregando ? "Carregando..." : "Atualizar"}
        </BtnFiltro>
      </FiltroBar>

      {itens.length > 0 && (
        <Resumo>
          <ResumoItem>
            <span>Quantidade</span>
            <span>{itens.length}</span>
          </ResumoItem>
          <ResumoItem>
            <span>Total</span>
            <span>{fmtValor(totalValor)}</span>
          </ResumoItem>
        </Resumo>
      )}

      {carregando ? (
        <Vazio>Carregando...</Vazio>
      ) : itens.length === 0 ? (
        <Vazio>Nenhum registro encontrado.</Vazio>
      ) : (
        <Tabela>
          <thead>
            <tr>
              <Th>#</Th>
              <Th>Tipo</Th>
              <Th>Usuário</Th>
              <Th>Valor</Th>
              <Th>Chave PIX</Th>
              <Th>Status</Th>
              <Th>Data</Th>
              {filtro === "pendentes" && <Th>Ação</Th>}
            </tr>
          </thead>
          <tbody>
            {itens.map((item, idx) => (
              <Tr key={`${item.tipo}-${item.id}`} $par={idx % 2 === 0}>
                <Td>{item.id}</Td>
                <Td>
                  <TipoBadge $tipo={item.tipo}>
                    {item.tipo === "DEPOSITO" ? "Depósito" : "Saque"}
                  </TipoBadge>
                </Td>
                <Td>{item.nomeUsuario || "-"}</Td>
                <Td><strong>{fmtValor(item.valor)}</strong></Td>
                <Td style={{ fontSize: "12px", wordBreak: "break-all" }}>
                  {item.chavePix || "-"}
                </Td>
                <Td><Badge $status={item.status}>{item.status}</Badge></Td>
                <Td style={{ fontSize: "12px" }}>{fmtData(item.dataRef)}</Td>
                {filtro === "pendentes" && (
                  <Td>
                    {item.tipo === "SAQUE" ? (
                      <BtnAprovar
                        onClick={() => aprovarSaque(item.id)}
                        disabled={!!processando[item.id]}
                      >
                        {processando[item.id] ? "..." : "Aprovar"}
                      </BtnAprovar>
                    ) : (
                      <span style={{ color: "#aaa", fontSize: "12px" }}>-</span>
                    )}
                  </Td>
                )}
              </Tr>
            ))}
          </tbody>
        </Tabela>
      )}
    </Container>
  );
}
